import { useEffect, useState } from "react";
import { Link } from "wouter";
import { trpc } from "../lib/trpc";
import { useProgressStream, type ProgressEvent } from "../hooks/useSSE";
import { Button, Card, Badge } from "../components/ui";
import { JobProgress } from "../components/JobProgress";
import { fmtDateTime, timeAgo, fmtNum } from "../lib/format";

const JOB_LABEL: Record<string, string> = {
  pbs_sync: "PBS catalog sync",
  exact_snapshot: "ExactOnline snapshot",
  ls_snapshot: "Lightspeed snapshot",
  reconcile: "Reconcile",
  apply: "Apply change set",
};
const JOB_TYPES = ["pbs_sync", "exact_snapshot", "ls_snapshot", "reconcile", "apply"] as const;
const JOB_STATUSES = ["running", "completed", "failed", "cancelled"] as const;
const inputCls = "rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200";

const asObj = (v: unknown): Record<string, unknown> => {
  if (v && typeof v === "object") return v as Record<string, unknown>;
  if (typeof v === "string") { try { return JSON.parse(v) as Record<string, unknown>; } catch { return {}; } }
  return {};
};
const targetLabel = (t: unknown) => (t === "lightspeed" ? "Lightspeed" : t === "exact" ? "ExactOnline" : null);

/** "Apply change set" rows: link to the change set + show its target, instead of a bare label. */
function ApplyJobLabel({ totals }: { totals: unknown }) {
  const t = asObj(totals);
  const id = t.changeSetId;
  const tgt = targetLabel(t.target);
  if (id == null) return <>Apply change set</>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Link href={`/reconcile/${String(id)}`} className="font-medium text-indigo-600 hover:underline">Apply #{String(id)}</Link>
      {tgt && <span className="text-[10px] uppercase text-slate-400">{tgt}</span>}
    </span>
  );
}
/** "3 applied · WOW865011, WOW965011 (+1)" for an apply job — shows WHICH items were posted. */
function applySummary(totals: unknown): string {
  const t = asObj(totals);
  const parts: string[] = [];
  if (t.dryRun) parts.push("dry-run");
  const applied = Number(t.applied ?? 0);
  parts.push(`${applied} applied`);
  if (Number(t.failed ?? 0)) parts.push(`${Number(t.failed)} failed`);
  const skus = Array.isArray(t.skus) ? (t.skus as unknown[]).map(String) : t.sku ? [String(t.sku)] : [];
  if (skus.length) {
    const more = applied > skus.length ? ` +${applied - skus.length}` : "";
    parts.push(`${skus.slice(0, 6).join(", ")}${more}`);
  }
  return parts.join(" · ") || "—";
}

/** A clean, curated one-line result per job type (no raw object dumps / internal fields). */
function summarizeTotals(type: string, totals: unknown): string {
  const t = asObj(totals);
  const num = (k: string) => Number(t[k] ?? 0);
  const join = (parts: (string | null | false)[]) => parts.filter(Boolean).join(" · ") || "—";
  if (type === "reconcile") {
    const counts = (["create", "update", "visibility", "price", "excluded", "orphanDiscontinued"] as const)
      .filter((k) => num(k) > 0)
      .map((k) => `${num(k).toLocaleString("nl-NL")} ${k === "orphanDiscontinued" ? "orphan-discontinue" : k}`);
    return join([t.total != null ? `${num("total").toLocaleString("nl-NL")} ops` : null, ...counts, t.requiresApproval ? "approval required" : null]);
  }
  if (type === "pbs_sync") return join([t.products != null && `${num("products").toLocaleString("nl-NL")} products`, t.categories != null && `${num("categories").toLocaleString("nl-NL")} categories`]);
  if (type === "exact_snapshot") return join([t.mode ? String(t.mode) : null, t.supplierItems != null && `${num("supplierItems").toLocaleString("nl-NL")} supplier items`, t.exactItems != null && `${num("exactItems").toLocaleString("nl-NL")} items`, t.salesPrices != null && `${num("salesPrices").toLocaleString("nl-NL")} prices`]);
  if (type === "ls_snapshot") return join([t.variants != null && `${num("variants").toLocaleString("nl-NL")} variants`, num("categories") > 0 && `${num("categories").toLocaleString("nl-NL")} categories`, num("brands") > 0 && `${num("brands").toLocaleString("nl-NL")} brands`]);
  // generic: only scalar, non-noisy keys
  const hide = new Set(["changeSetId", "byReason", "requiresApproval", "maxPriceDeltaPct", "pbsCatalogSize", "orphanCheckSkipped", "orphanSkipReason", "status", "dryRun", "target"]);
  return join(
    Object.entries(t)
      .filter(([k, v]) => !hide.has(k) && (typeof v === "number" || typeof v === "string") && String(v).length < 40)
      .slice(0, 6)
      .map(([k, v]) => `${k}: ${typeof v === "number" ? v.toLocaleString("nl-NL") : v}`),
  );
}

function ProgressBar({ ev }: { ev?: ProgressEvent }) {
  if (!ev) return <p className="text-sm text-slate-400">Idle.</p>;
  const pct = ev.percent < 0 ? 100 : ev.percent;
  const error = ev.percent < 0;
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs text-slate-500">
        <span>{ev.step}{ev.detail ? ` — ${ev.detail}` : ""}</span>
        <span>{error ? "error" : `${pct}%`} · {ev.memoryMB.rss} MB</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full ${error ? "bg-red-500" : "bg-slate-800"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Live stage + mini progress bar for a job that's currently RUNNING (from the SSE stream). */
function RunningStage({ ev, startedAt, durablePercent, durableStep }: { ev?: ProgressEvent; startedAt?: string | Date | null; durablePercent?: number | null; durableStep?: string | null }) {
  const elapsedMs = startedAt ? Date.now() - new Date(startedAt).getTime() : 0;
  const mins = Math.floor(elapsedMs / 60000);
  const elapsedTxt = startedAt ? (mins >= 1 ? `${mins}m` : `${Math.max(0, Math.round(elapsedMs / 1000))}s`) : "";
  if (!ev) {
    // No live SSE event in THIS browser's stream. A DETACHED worker (e.g. an apply) emits its progress in
    // its own process, so the live stream never carries it — but the worker persists progress to the job
    // row every ~2s. Fall back to that durable %/step so the row shows the REAL state, not "starting…".
    if (durablePercent != null && (durablePercent > 0 || durableStep)) {
      const pct = Math.max(0, Math.min(100, durablePercent));
      return (
        <div className="min-w-[240px]">
          <div className="mb-1 flex justify-between text-xs text-slate-500">
            <span className="truncate pr-2">{durableStep || "working…"}</span>
            <span className="tabular-nums">{pct}%{elapsedTxt ? ` · ${elapsedTxt}` : ""}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-slate-800 transition-all" style={{ width: `${Math.max(2, pct)}%` }} />
          </div>
        </div>
      );
    }
    // No live progress and nothing persisted yet. If it's been a while, the job was almost certainly
    // interrupted (e.g. an app restart) — it auto-clears to "failed" shortly. Say so instead of "working…".
    return elapsedMs > 120_000 ? (
      <span className="text-amber-700">running {elapsedTxt} · no recent progress — auto-clears if interrupted</span>
    ) : (
      <span className="text-amber-600">starting… {elapsedTxt}</span>
    );
  }
  // Use the job row's startedAt when the event doesn't carry one (sync jobs), so elapsed/ETA always show.
  const evWithStart: ProgressEvent = ev.startedAt ? ev : { ...ev, startedAt: startedAt ? new Date(startedAt).getTime() : undefined };
  return (
    <div className="min-w-[240px]">
      <JobProgress ev={evWithStart} compact />
    </div>
  );
}

export default function Sync() {
  const utils = trpc.useUtils();
  const status = trpc.pbs.syncStatus.useQuery(undefined, { refetchInterval: 4000 });
  const snaps = trpc.snapshots.status.useQuery(undefined, { refetchInterval: 5000 });
  const sched = trpc.system.schedules.useQuery(undefined, { refetchInterval: 60_000 });

  // Sync-history filters + pagination (server-side, so you can explore the full past).
  const [type, setType] = useState("");
  const [hstatus, setHstatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // debounced
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Debounce the search box (avoid a query per keystroke) and reset to page 1 on any new search term.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  // Any other filter change also returns to page 1.
  useEffect(() => { setPage(1); }, [type, hstatus, from, to, pageSize]);

  const hasFilters = !!(type || hstatus || search || from || to);
  const history = trpc.pbs.syncHistory.useQuery(
    {
      type: (type || undefined) as (typeof JOB_TYPES)[number] | undefined,
      status: (hstatus || undefined) as (typeof JOB_STATUSES)[number] | undefined,
      search: search || undefined,
      from: from || undefined,
      to: to || undefined,
      page,
      pageSize,
    },
    { refetchInterval: 6000 },
  );
  const { byJob } = useProgressStream();
  const budget = trpc.system.apiBudget.useQuery(undefined, { refetchInterval: 5000 });

  const startSync = trpc.pbs.sync.useMutation({ onSuccess: () => utils.pbs.syncStatus.invalidate() });
  const refreshExact = trpc.snapshots.refreshExact.useMutation({ onSuccess: () => utils.snapshots.status.invalidate() });
  const refreshLs = trpc.snapshots.refreshLs.useMutation({ onSuccess: () => utils.snapshots.status.invalidate() });
  const cancelRunning = trpc.snapshots.cancelRunning.useMutation({
    onSuccess: () => {
      utils.pbs.syncHistory.invalidate();
      utils.snapshots.status.invalidate();
      utils.pbs.syncStatus.invalidate();
    },
  });
  const runningCount = history.data?.running ?? 0; // global (ignores filters) so Cancel always shows when needed
  const rows = history.data?.rows ?? [];
  const total = history.data?.total ?? 0;
  const pageCount = history.data?.pageCount ?? 1;
  // If a background refetch (every 6s) shrinks the result set below the current page — e.g. filtering by
  // "running" while jobs finish — snap back into range, else we'd show a phantom empty page with the
  // Prev/Next controls hidden (they live in the non-empty branch). pageCount has a floor of 1.
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [pageCount, page]);
  const clearFilters = () => { setType(""); setHstatus(""); setSearchInput(""); setSearch(""); setFrom(""); setTo(""); setPage(1); };

  // Live stage for a running row: the SSE event if we have it, else the server's last-known progress
  // (survives a page reload; snapshots persist it) so the row never just says "starting…".
  const runningStage = (type: string): ProgressEvent | undefined => {
    if (byJob[type]) return byJob[type];
    const p = type === "exact_snapshot" ? snaps.data?.progress?.exact : type === "ls_snapshot" ? snaps.data?.progress?.ls : null;
    return p ? (p as unknown as ProgressEvent) : undefined;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Sync &amp; Data</h1>

      <div className="grid gap-4 md:grid-cols-3">
        <Card title="PBS catalog" actions={<Button onClick={() => startSync.mutate()} disabled={status.data?.running}>{status.data?.running ? "Running…" : "Sync now"}</Button>}>
          <p className="mb-3 text-sm text-slate-500">{fmtNum(status.data?.products)} products · {fmtNum(status.data?.categories)} categories · updated {timeAgo(status.data?.lastJob?.completedAt)}{sched.data?.pbs.nextRun && <> · <span title={`Scheduled run (${sched.data.pbs.cron}) — managed in Settings → Schedules`}>next run {fmtDateTime(sched.data.pbs.nextRun)}</span></>}</p>
          <ProgressBar ev={byJob["pbs_sync"]} />
        </Card>

        <Card title="ExactOnline snapshot" actions={
          <div className="flex gap-1.5">
            <Button variant="secondary" onClick={() => refreshExact.mutate({})} title="Incremental — fetch only ExactOnline changes since the last run (fast, few API calls)">Refresh</Button>
            <Button variant="ghost" onClick={() => { if (confirm("Force a FULL ExactOnline resync? This re-reads the whole catalog (heavier, more API calls). Normally not needed — the weekly full runs automatically.")) refreshExact.mutate({ full: true }); }} title="Force a full resync of the whole ExactOnline catalog">Full</Button>
          </div>
        }>
          <p className="mb-3 text-sm text-slate-500">{fmtNum(snaps.data?.exact.total)} items · updated {timeAgo(snaps.data?.exact.fetchedAt)}{sched.data?.snapshots.nextRun && <> · <span title={`Scheduled run (${sched.data.snapshots.cron}) — managed in Settings → Schedules`}>next run {fmtDateTime(sched.data.snapshots.nextRun)}</span></>}</p>
          <ProgressBar ev={byJob["exact_snapshot"]} />
        </Card>

        <Card title="Lightspeed snapshot" actions={<Button variant="secondary" onClick={() => refreshLs.mutate()}>Refresh</Button>}>
          <p className="mb-3 text-sm text-slate-500">{fmtNum(snaps.data?.lightspeed.total)} variants · updated {timeAgo(snaps.data?.lightspeed.fetchedAt)}{sched.data?.byId?.["ls-snapshot"]?.nextRun && <> · <span title={`Scheduled run (${sched.data.byId["ls-snapshot"].cron}) — managed in Settings → Schedules`}>next run {fmtDateTime(sched.data.byId["ls-snapshot"].nextRun)}</span></>}</p>
          <ProgressBar ev={byJob["ls_snapshot"]} />
        </Card>
      </div>

      <Card title="API limits (today)">
        {budget.data ? (
          <div className="grid gap-5 sm:grid-cols-2">
            {(["exact", "lightspeed"] as const).map((tg) => {
              const b = budget.data![tg];
              const real = b.real;
              // ExactOnline: live provider quota from the MCP proxy (real). Lightspeed: our configured budget.
              const usedPct = real
                ? real.dailyLimit
                  ? Math.min(100, Math.round(((real.dailyLimit - real.dailyRemaining) / real.dailyLimit) * 100))
                  : 0
                : b.perDay
                  ? Math.min(100, Math.round((b.actualToday / b.perDay) * 100))
                  : 0;
              const near = usedPct >= 80;
              return (
                <div key={tg}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{tg === "lightspeed" ? "Lightspeed" : "ExactOnline"}</span>
                    <span className="flex items-center gap-1.5">
                      {real && <Badge color="green">live</Badge>}
                      {real?.maintenance && <Badge color="amber">maintenance</Badge>}
                      {b.circuitOpen && <Badge color="red">paused — recent errors</Badge>}
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full ${near ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.max(2, usedPct)}%` }} />
                  </div>
                  {real ? (
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums text-slate-500">
                      <span className={near ? "font-semibold text-amber-700" : "font-semibold text-slate-700"}>{real.dailyRemaining.toLocaleString("nl-NL")}</span>
                      <span className="-ml-2">/ {real.dailyLimit.toLocaleString("nl-NL")} left today</span>
                      <span>{real.minuteRemaining.toLocaleString("nl-NL")}/{real.minuteLimit.toLocaleString("nl-NL")} this minute</span>
                      <span className="text-slate-400">{b.actualToday.toLocaleString("nl-NL")} of these were ours · {real.healthy}/{real.connections} conn.</span>
                    </div>
                  ) : (
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums text-slate-500">
                      <span><span className="font-semibold text-slate-700">{b.actualToday.toLocaleString("nl-NL")}</span> / {b.perDay.toLocaleString("nl-NL")} today</span>
                      <span className={near ? "font-semibold text-amber-700" : ""}>{b.remainingToday.toLocaleString("nl-NL")} left</span>
                      <span>{b.usedThisMinute}/{b.perMinute} this minute</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-400">Loading…</p>
        )}
        <p className="mt-3 text-[11px] text-slate-400">ExactOnline shows the provider's <span className="font-medium text-slate-500">live</span> remaining quota, read through the MCP proxy (summed across its connections) — its real daily &amp; per‑minute allowance. Lightspeed shows your configured budget (Settings → Budgets); "today" counts actual calls since midnight. The bar turns amber past 80% used.</p>
      </Card>

      <Card
        title="Sync history"
        actions={
          runningCount > 0 ? (
            <Button
              variant="danger"
              disabled={cancelRunning.isPending}
              onClick={() => {
                if (confirm(`Cancel ${runningCount} running job${runningCount > 1 ? "s" : ""} and free all locks? Use this if a job is stuck. (It won't undo work already written, just clears the status so you can start a fresh run.)`))
                  cancelRunning.mutate();
              }}
              title="Force-cancel all running jobs and release their locks"
            >
              {cancelRunning.isPending ? "Cancelling…" : `Cancel running (${runningCount})`}
            </Button>
          ) : undefined
        }
      >
        {/* Filter bar */}
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Job
            <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All jobs</option>
              {JOB_TYPES.map((t) => <option key={t} value={t}>{JOB_LABEL[t]}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Status
            <select className={inputCls} value={hstatus} onChange={(e) => setHstatus(e.target.value)}>
              <option value="">All statuses</option>
              {JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            From
            <input type="date" className={inputCls} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            To
            <input type="date" className={inputCls} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label className="flex flex-1 flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Search (SKU / error)
            <input type="text" placeholder="e.g. 933270 or a word from an error" className={`${inputCls} min-w-[180px]`} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </label>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} title="Clear all filters">Clear</Button>
          )}
        </div>

        {history.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">{hasFilters ? "No jobs match these filters." : "No syncs yet."}</p>
        ) : (
          <>
            <table className="w-full table-fixed text-left text-sm">
              <colgroup><col className="w-40" /><col className="w-44" /><col className="w-28" /><col /><col className="w-56" /></colgroup>
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="py-2 font-medium">Started</th><th className="font-medium">Job</th><th className="font-medium">Status</th><th className="font-medium">Result</th><th className="font-medium">Error</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((j) => (
                  <tr key={j.id} className={`align-top ${j.status === "running" ? "bg-amber-50/50" : ""}`}>
                    <td className="py-2 pr-2 text-slate-600 whitespace-nowrap">{fmtDateTime(j.startedAt)}</td>
                    <td className="pr-2 text-slate-700">{j.type === "apply" ? <ApplyJobLabel totals={j.totals} /> : (JOB_LABEL[j.type] ?? j.type)}</td>
                    <td>
                      {j.status === "running" ? (
                        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" /><Badge color="amber">running</Badge></span>
                      ) : (
                        <Badge color={j.status === "completed" ? "green" : j.status === "failed" || j.status === "cancelled" ? "red" : "amber"}>{j.status}</Badge>
                      )}
                    </td>
                    <td className="break-words pr-2 text-slate-600">{j.status === "running" ? <RunningStage ev={runningStage(j.type)} startedAt={j.startedAt} durablePercent={j.progressPercent} durableStep={j.progressStep} /> : j.type === "apply" ? applySummary(j.totals) : summarizeTotals(j.type, j.totals)}</td>
                    <td className="break-words text-xs text-red-600">{j.error ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination footer */}
            <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
              <span>
                {total.toLocaleString("nl-NL")} result{total === 1 ? "" : "s"}
                {pageCount > 1 ? ` · page ${page} of ${pageCount}` : ""}
              </span>
              <div className="flex items-center gap-2">
                <select className={inputCls} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} title="Rows per page">
                  {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</Button>
                <Button variant="secondary" size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next →</Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
